import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getUser, getDashboardPath } from '../../../utils/auth';
import { toast } from '../../ui/sonner';

const API = window.location.hostname === 'localhost' ? 'http://localhost:8001/api' : '/api';

// Gate for the landing page rendered at "/".
// 1. Logged-in users are redirected to their dashboard (preserves old RootRedirect behavior).
// 2. Handles OAuth callbacks that land on "/" (?oauth_success / ?error / #session_id).
// Returns true while a redirect is pending, so the page can render nothing until resolved.
export default function useLandingGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  useEffect(() => {
    // Already authenticated → straight to dashboard.
    if (user) {
      navigate(getDashboardPath(), { replace: true });
      return;
    }

    const params = new URLSearchParams(window.location.search);

    if (params.get('oauth_success') === 'true') {
      axios
        .get(`${API}/auth/me`, { withCredentials: true })
        .then((res) => {
          toast.success('Welcome back!');
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate(getDashboardPath(res.data), { replace: true });
        })
        .catch(() => navigate('/dashboard', { replace: true }));
      return;
    }

    if (params.get('error')) {
      toast.error(`Login failed: ${params.get('error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      if (sessionId) {
        axios
          .post(`${API}/auth/oauth/session`, {}, { headers: { 'X-Session-ID': sessionId } })
          .then((res) => {
            toast.success('Welcome back!');
            window.history.replaceState({}, document.title, window.location.pathname);
            navigate(getDashboardPath(res.data?.user), { replace: true });
          })
          .catch(() => toast.error('Authentication failed. Please try again.'));
      }
    }
  }, [user, navigate, location]);

  return Boolean(user);
}
